/**
 * Cookie consent, the CNIL way, without any third party: the application sets
 * no advertising or tracking cookie and calls no analytics. What it keeps in
 * the browser falls in two groups:
 *
 * - **necessary**: the SSO tokens that keep the person signed in, and this very
 *   choice. Always on, the application does not work without them;
 * - **comfort**: the language, the theme, the sidebar state, the recent searches
 *   and the local API statistics. Written to disk only once the person agreed;
 *   until then (or after a refusal) they live in memory for the page only.
 *
 * `safeStorage` enforces the split on every write; the dialog in
 * `components/common/CookieConsent` collects the choice; the sidebar reopens it.
 * The identity server (Keycloak) sets its own session cookies on its domain.
 */
export const CONSENT_KEY = 'anep-cookie-consent';
export const CONSENT_VERSION = 1;
/** The CNIL asks that consent be collected again within thirteen months at most; six keeps a margin. */
export const CONSENT_MAX_AGE_DAYS = 180;

export interface CookieConsent {
  version: number;
  /** ISO date of the choice. */
  date: string;
  /** Whether comfort settings may be kept on disk. */
  preferences: boolean;
}

/** The browser-storage keys that only comfort requires (see the modules that own them). */
export const COMFORT_KEYS: readonly string[] = [
  'preferred-language',          // I18nContext
  'theme-config',                // contexts/theme/themeStorage
  'ui-prefs-theme-customised',   // contexts/theme/themeStorage
  'sidebar-collapsed',           // LayoutContext
  'gsearch.recent',              // navigation/GlobalSearch
  'api_stats',                   // services/apiStatsService
];

export const isComfortKey = (key: string): boolean => COMFORT_KEYS.includes(key);

type Listener = (consent: CookieConsent | null) => void;
const listeners = new Set<Listener>();

function disk(): Storage | null {
  try {
    return typeof window !== 'undefined' ? window.localStorage : null;
  } catch {
    return null;
  }
}

/** The choice on record, or null when there is none, it is too old, or it predates this version of the dialog. */
export function readConsent(): CookieConsent | null {
  const store = disk();
  if (!store) return null;
  try {
    const parsed = JSON.parse(store.getItem(CONSENT_KEY) || 'null');
    if (!parsed || parsed.version !== CONSENT_VERSION || typeof parsed.preferences !== 'boolean' || typeof parsed.date !== 'string') return null;
    const ageDays = (Date.now() - new Date(parsed.date).getTime()) / 86_400_000;
    if (!Number.isFinite(ageDays) || ageDays > CONSENT_MAX_AGE_DAYS) return null;
    return { version: parsed.version, date: parsed.date, preferences: parsed.preferences };
  } catch {
    return null;
  }
}

export function writeConsent(preferences: boolean): CookieConsent {
  const consent: CookieConsent = { version: CONSENT_VERSION, date: new Date().toISOString(), preferences };
  try {
    disk()?.setItem(CONSENT_KEY, JSON.stringify(consent));
  } catch {
    // Storage refused: the choice holds for this page, the dialog comes back next time.
  }
  listeners.forEach((fn) => fn(consent));
  return consent;
}

export function clearConsent(): void {
  try {
    disk()?.removeItem(CONSENT_KEY);
  } catch {
    // nothing to clear
  }
  listeners.forEach((fn) => fn(null));
}

export const preferencesAllowed = (): boolean => readConsent()?.preferences === true;

/** Called with every new choice; returns the unsubscribe. */
export function onConsentChange(fn: Listener): () => void {
  listeners.add(fn);
  return () => { listeners.delete(fn); };
}

/** The sidebar link and the dialog talk through this window event: show the settings again. */
export const OPEN_SETTINGS_EVENT = 'anep:cookie-settings';

export function openCookieSettings(): void {
  window.dispatchEvent(new CustomEvent(OPEN_SETTINGS_EVENT));
}
